// useBulkStatus — Phase 6b: apply a status transition to N conversations at once.
//
// Wraps POST /api/conversations/bulk-status. The backend caps the batch at
// 100; the Standup UI never sends more than the 12 visible decide-cards +
// the hidden-spam drawer anyway, so we never realistically bump against that.
//
// On success we invalidate the Standup bucket query so the selected threads
// move to their new bucket immediately. We do NOT invalidate the single
// `['conversation', id]` queries because the user isn't viewing any of them
// from Standup, and invalidating 12 of them for nothing is wasteful.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ConversationStatus } from '@md/shared';
import { apiPost } from '@/lib/api';

export type BulkStatusResponse = {
  ok: true;
  requested: number;
  updated: number;
  status: ConversationStatus;
  snoozed_until: string | null;
};

export type BulkStatusVars = {
  ids: string[];
  status: ConversationStatus;
  /** ISO timestamp; required iff status='snoozed'. */
  snoozed_until?: string | null;
};

type BulkStatusBody = {
  ids: string[];
  status: ConversationStatus;
  snoozed_until?: string | null;
};

export function useBulkStatus() {
  const qc = useQueryClient();
  return useMutation<BulkStatusResponse, Error, BulkStatusVars>({
    mutationFn: (vars) =>
      apiPost<BulkStatusResponse, BulkStatusBody>(
        '/api/conversations/bulk-status',
        {
          ids: vars.ids,
          status: vars.status,
          snoozed_until: vars.status === 'snoozed' ? vars.snoozed_until ?? null : null,
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}
